/* ================================================================
 * Tour stop · Deterministic PRNG and the "no Math.random" rule
 * ----------------------------------------------------------------
 * See docs/code-roadmap.md §5.2 for the surrounding narrative.
 *
 * `Math.random()` is poison in this codebase. It would silently
 * destroy replay determinism: same seed, same inputs, different
 * outcomes depending on browser, page-load order, even the moon.
 * The boundary lint at tools/check-imports.ts greps every file in
 * packages/sim and packages/bots and FAILS THE BUILD if it sees
 * `Math.random` anywhere. This file is the sanctioned alternative.
 *
 * The algorithm is mulberry32: a small, fast 32-bit PRNG. It takes
 * a single uint32 of state and produces uint32 outputs. The arithmetic
 * is deliberately bitwise so the JavaScript engine has no wiggle room
 * to produce different results across machines:
 *
 *  - `(x + y) | 0`    forces the result into a signed 32-bit int.
 *  - `x >>> 15`        unsigned right shift; coerces to uint32 first.
 *  - `Math.imul(a, b)` 32-bit integer multiplication (NOT regular `*`,
 *                      which would overflow into float64 land).
 *  - `value >>> 0`     final coerce back to uint32 for division.
 *
 * If you've never seen `>>> 0` before: that's the idiomatic way to
 * say "interpret this as an unsigned 32-bit integer." It is a hot
 * spot in any deterministic JS code that has to behave like C.
 *
 * Usage pattern across the sim: every function that needs randomness
 * takes an `RngState` parameter (almost always `state.rng`) and
 * advances it. The state lives ON the match state, so it travels
 * with snapshots, hashes into the per-tick fingerprint, and replays
 * exactly. There is no global RNG; there are no "convenience"
 * helpers that fetch a hidden one.
 *
 * If you add a new sim function that needs randomness, accept
 * `rng: RngState` as a parameter and call `nextFloat(rng)`. Do not
 * import the createRng / state machinery into transient scopes;
 * always thread the same rng through.
 * ================================================================ */

// Seeded deterministic PRNG (mulberry32). The ONLY source of randomness allowed
// in @arcade-hockey/sim and @arcade-hockey/bots. The boundary lint enforces
// "no Math.random" in those packages.

export type RngState = {
  // Mutable counter — caller passes the same object back to advance.
  state: number;
};

export function createRng(seed: number): RngState {
  // Normalize seed to a uint32 for stable behavior across positive/negative inputs.
  return { state: seed >>> 0 };
}

export function nextFloat(rng: RngState): number {
  // mulberry32: https://github.com/bryc/code/blob/master/jshash/PRNGs.md
  let t = (rng.state + 0x6d2b79f5) | 0;
  rng.state = t;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export function nextInt(rng: RngState, minInclusive: number, maxExclusive: number): number {
  return minInclusive + Math.floor(nextFloat(rng) * (maxExclusive - minInclusive));
}

export function nextRange(rng: RngState, minInclusive: number, maxExclusive: number): number {
  return minInclusive + nextFloat(rng) * (maxExclusive - minInclusive);
}

export function nextAngleOffset(rng: RngState, halfRangeRadians: number): number {
  return nextRange(rng, -halfRangeRadians, halfRangeRadians);
}
